/**
 * Step2Page.tsx - Página principal del Step 2 (Valida)
 * 
 * UID-02c | Fase 2 | HTML Canónico: P2 (Valida v9)
 *
 * Integra (P2RED-S1-2 · lienzo único, sin pestañas):
 * - WorkspaceShell map-céntrico: mapa protagonista + ficha derecha (S1-1) +
 *   tabla canónica inferior (buscador §6, chips-contador §7, copiar/lápiz §11.2)
 * - Modo «Ubicar en el mapa» desde la ficha (S1-4b, canal C2 'map')
 * - ProgressBar del documento (la identidad del documento vive en la tira
 *   bajo la fila de pasos; la ContextBar se desmontó en P2CTX)
 *
 * @migrated 2026-01-12 - Tokens centralizados del Design System
 */

import { useState, useMemo, useCallback, useRef, useEffect, type FC, type CSSProperties } from 'react';
import { InfoBanner, BannerStrong } from '../shell/InfoBanner';
import './step2-no-glow.css';
// P2RED-S1-2 · RED-4: la navegación por pestañas se retira de la superficie
// viva. Mapa de migración por pestaña (decisión P2RED-M2, STATUS 14-jul):
// - Sin geolocalizar / Revisar → filtros-chip de estado de la tabla canónica
//   (esta rebanada). [S1-4b: puente «Sin geolocalizar» RETIRADO por completo
//   (DOP-S1-4B-PUENTE=a, Luis 15-jul) — la auditoría 0.5 verificó que
//   aplicar-candidato estaba doblemente muerto en la ruta viva (sin handler en
//   WizardPageV2 + `candidates: []` hardcodeado en adaptStep2Props) y que
//   edición manual y confirmar ya viven en la ficha (S1-4a/S1-3b). Cierra
//   OBS-P2-PUENTE-SINGEO-RETIRAR-S13.]
// - Correcciones → edición inline ⚙ + «Historial de cambios» de la ficha (S1-3).
// - Sugerencias IA → tarjeta ✦ en la ficha (S1-3; la V1 se poda en S4).
// - Abreviaturas / Diccionario → tarjetas de regla β (S1-3).
// Los componentes de pestaña se podaron en S4 (`S4PODA`, 11-sep-2026); sus tipos
// viven en `./types` y siguen alimentando la interfaz pública (props conservadas
// como @deprecated para no romper consumidores).
import type {
  TabId,
  ValidationCounts,
  SinGeoFilterCounts as FilterCounts,
  ReviewItem,
  ReviewFilterCounts,
  AbreviaturaData,
  AbrevFilterCounts,
  DiccionarioEntry,
  DictFilterCounts,
  CorrectionData,
  CorrFilterCounts,
  SuggestionData,
  IAFilterCounts,
  InfrastructureData,
} from './types';
import type { ProcessedInfrastructure } from '@/types/processing';

// A3x-1b · Camino 2: shell map-céntrico con lienzo MapViewer overview (solo lectura).
// A3x-2 · tabla de atributos (display) en `panelInf`, alimentada por el adaptador.
import {
  WorkspaceShell, AttributeTable, StatusBar, BuscadorGlobal, normalizarBusqueda,
  toFilasAtributo, useSeleccionMapaTabla, type Coord25830, type FiltroEstado,
} from '../workspace';
// P2RED S1-1 · esqueleto del lienzo único: ficha del elemento en panel derecho (RED-1=a).
import { FichaPanel, type FichaElemento } from './FichaPanel';
import { contarEstadosFila } from '../shared/estadoFila';
import { aptitudAlbergue } from '@/lib/albergue/aptitudAlbergue';
import { capacidadAlbergue } from '@/lib/albergue/capacidadAlbergue';
import { mapTipologia, toProcessedRecords } from '@/services/export/toProcessedRecord';
import { getOpenRTAR2Service, type ConsultaOpenRTA } from '@/services/geocoding/specialized/OpenRTAR2Service';
import {
  plazasRegistroPorFila, esAlojamientoPorNombre, type PlazasRegistroFila,
} from '@/lib/albergue/alojamientosRTA';
// P2RED S1-5a · candidatos on-demand del elemento seleccionado (DOP-S1-5A-ALCANCE=c).
import { useCandidatosElemento, aplicarCandidato } from './useCandidatosElemento';
// P2RED S1-3a · contrato `Propuesta` (view-model §3 del AUDIT P2DPROP) + sección de la ficha.
import {
  proyectarPropuestas,
  type ExcepcionElemento,
  type Propuesta,
  type PropuestaStatus,
} from './propuestas';
import { PropuestasSection } from './PropuestasSection';
// P2RED S1-3b · secciones Coordenadas (botonera, DOP-1=a) e Historial (solo
// lectura Worker /coord-edits, DOP-2=a) + pie de ficha «Confirmar elemento».
import { CoordenadasSection, type CoordenadasSectionHandle } from './CoordenadasSection';
import { HistorialSection } from './HistorialSection';
import { CatastroInlinePanel } from './CatastroInlinePanel';
// CATUSO · tipología PTEL del elemento para el aviso por uso de la ranura Catastro.
import { infraTipologiaAPTEL } from '@/types/catastroR2';
import { avisoCentroide, deriveProcedencia, VIA_LABELS_LARGO } from '@/utils/procedenciaVia';
import { MapViewer } from '@/components/map/MapViewer';
import { toNormalizedInfrastructures } from '@/components/map/MapViewer/adapters';
import { pointToUTM30, pointToWGS84 } from '@/lib/leaflet-crs';
// P2RED S1-5b · contrato del marcador candidato del lienzo (import directo del
// módulo, tipo-only; el barrel no lo re-exporta aún).
import type { CandidatoMapa } from '@/components/map/MapViewer/MapViewer';
import type { MapPoint } from '@/components/map/MapContainer';

import { colors, typography, borderRadius, spacing, transitions } from '@/design-system/v2';
import { usePermissions } from '@/hooks/usePermissions';
import { getCodigoINE } from '@/lib/codigosINEUnificado';
import {
  escucharEstadoContorno,
  textoSinValidarContorno,
  type BoundaryLoadStatus,
} from '@/services/boundaries/MunicipalBoundaryService';

/** Sin equiv. v2 — constantes locales */
const GAP = { xs: '4px', sm: '6px', md: '8px', lg: '12px', xl: '16px', '2xl': '20px' } as const;
const ICON_SIZE = { xs: 12, sm: 14, md: 16, lg: 18, xl: 20, '2xl': 24 } as const;

// ============================================================================
// TYPES
// ============================================================================

export interface DocumentContext {
  /** Nombre del archivo */
  fileName: string;
  /** Nombre del municipio */
  municipio: string;
  /** Provincia */
  provincia: string;
  /** Código INE */
  ine: string;
  /** Total de elementos en el documento */
  totalElements: number;
}

export interface ProgressData {
  /** Filas «Ubicada» (P2VOC-a: `clasificarEstadoFila`) */
  approved: number;
  /** Filas «Por revisar»: con coordenada, en revisión manual, baja confianza o centroide */
  pending: number;
  /** Filas «Sin coordenada»: falta X o Y */
  notGeolocated: number;
}

export interface Step2PageProps {
  /** Contexto del documento actual */
  documentContext: DocumentContext;
  /** Datos de progreso */
  progress: ProgressData;
  /** @deprecated P2RED-S1-2 (RED-4): ya no hay pestañas que contar. Sin efecto. */
  initialCounts?: Partial<ValidationCounts>;
  /** @deprecated P2RED-S1-4b (DOP-PUENTE=a): puente sin-geo retirado. Sin efecto. */
  sinGeoInfrastructures?: InfrastructureData[];
  /** Conjunto completo de infraestructuras resueltas (cascada geo>norm>raw); origen del overview de mapa (A3x-1a) */
  allInfrastructures?: ProcessedInfrastructure[];
  /** @deprecated P2RED-S1-4b (DOP-PUENTE=a): puente sin-geo retirado. Sin efecto. */
  sinGeoFilterCounts?: FilterCounts;
  /** Abreviaturas detectadas — P2RED-S1-3a: alimentan la proyección al contrato `Propuesta` (tarjetas de regla β en la ficha). */
  abreviaturas?: AbreviaturaData[];
  /** @deprecated P2RED-S1-2 (RED-4): pestaña retirada. Sin efecto. */
  abrevFilterCounts?: AbrevFilterCounts;
  /** Términos del diccionario — P2RED-S1-3a: alimentan la proyección al contrato `Propuesta` (tarjetas de regla β en la ficha). */
  diccionarioEntries?: DiccionarioEntry[];
  /** @deprecated P2RED-S1-2 (RED-4): pestaña retirada. Sin efecto. */
  dictFilterCounts?: DictFilterCounts;
  /** Correcciones del pipeline — P2RED-S1-3a: alimentan la proyección al contrato `Propuesta` (propuestas por elemento en la ficha). */
  corrections?: CorrectionData[];
  /** @deprecated P2RED-S1-2 (RED-4): pestaña retirada. Sin efecto. */
  corrFilterCounts?: CorrFilterCounts;
  /** @deprecated P2RED-S1-2 (RED-4): pestaña retirada; destino = filtros-chip de la tabla canónica. Sin efecto. */
  reviewItems?: ReviewItem[];
  /** @deprecated P2RED-S1-2 (RED-4): pestaña retirada. Sin efecto. */
  reviewFilterCounts?: ReviewFilterCounts;
  /** Sugerencias IA — P2RED-S1-3a: alimentan la proyección al contrato `Propuesta` (propuestas por elemento en la ficha). */
  suggestions?: SuggestionData[];
  /** @deprecated P2RED-S1-2 (RED-4): pestaña retirada. Sin efecto. */
  iaFilterCounts?: IAFilterCounts;
  /** @deprecated P2RED-S1-2 (RED-4): ya no hay pestañas. Sin efecto. */
  onTabChange?: (tabId: TabId) => void;
  /** @deprecated P2RED-S1-4b (DOP-PUENTE=a): puente sin-geo retirado (prop ya sin cablear en la ruta viva). Sin efecto. */
  onAddInfrastructure?: () => void;
  /** @deprecated P2RED-S1-4b (DOP-PUENTE=a): puente sin-geo retirado (prop ya sin cablear en la ruta viva). Sin efecto. */
  onExport?: () => void;
  /** @deprecated P2RED-S1-4b (DOP-PUENTE=a): puente retirado. S1-5a implementó el flujo vivo por otro cauce (useCandidatosElemento + canal C2 'assisted'); esta prop del árbol sin-geo queda muerta hasta la poda S4. */
  onApplyCandidate?: (infraId: string, candidateIndex: number) => void;
  /** @deprecated P2RED-S1-4b (DOP-PUENTE=a): puente retirado; la edición manual vive en la ficha (conversor S1-4a + «Ubicar» S1-4b). */
  onOpenManualEdit?: (infraId: string) => void;
  /** @deprecated P2RED-S1-4b (DOP-PUENTE=a): puente retirado; el editor C1 ya no tiene disparador en P2 (la ficha aplica por onApplyManualCoords). */
  onEditCoords?: (infraId: string) => void;
  /** P2-S3 · «Confirmar elemento» (pie de ficha, AUDIT modelo B §7.3) */
  onConfirmElement?: (infraId: string) => void;
  /** C1b: volcar coords del pin arrastrable (UTM30) → patchResult (handleApplyCoords) */
  onDragEditCoords?: (infraId: string, x: number, y: number) => void;
  /** S1-4a: aplicar coordenada del conversor in-ficha (canal C2, editSource 'manual').
   *  S1-5a: acepta `source` opcional — 'assisted' al aplicar un candidato (DOP-S1-5A-CANAL=a). */
  onApplyManualCoords?: (infraId: string, x: number, y: number, source?: 'manual' | 'map' | 'assisted') => void;
  /** @deprecated P2RED-S1-4b (DOP-PUENTE=a): puente sin-geo retirado (prop ya sin cablear en la ruta viva). Sin efecto. */
  onOpenViewer?: (infraId: string) => void;
  /** Modo demo — muestra banner informativo */
  isDemo?: boolean;
  /** Callback al continuar al paso 3 (Coteja) */
  onContinue?: () => void;
  /**
   * GUIA-DEVOLUCION-02: error dominante detectado en el ODT.
   * Si no es null se muestra un banner ámbar inline con la instrucción al técnico.
   */
  rejectionBanner?: {
    code: string;
    percentage: number;
    instruccionEspecifica: string;
  } | null;
  /**
   * BUG-UI-16: grupos de infraestructuras que comparten exactamente las mismas
   * coordenadas UTM (≥ 3 infras en el mismo punto). Se muestra un banner ámbar
   * para que el técnico revise si son centroides o errores de geocodificación.
   */
  duplicateCoordsWarning?: Array<{
    /** Coordenadas compartidas (UTM30 EPSG:25830) */
    x: number;
    y: number;
    /** Nombres de las infraestructuras afectadas */
    names: string[];
  }>;
}

// ============================================================================
// STYLES (usando tokens centralizados)
// ============================================================================

const containerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 0,
};

// A3x-1b · Camino 2: estilo del panel de trabajo (tabs) dentro de `panelIzq` del
// WorkspaceShell. Sin `margin:'0 56px'` (ya no es full-width) ni `flex:1` (el
// `<aside>` del shell aporta alto + scroll). Conserva el padding del contenido.
const panelTrabajoStyle: CSSProperties = {
  padding: '20px 24px',
  paddingBottom: '80px',
};

// S1-4b · envoltorio del lienzo + chip del modo «Ubicar en el mapa». El chip
// no captura punteros (los clics atraviesan hasta el lienzo) y flota sobre los
// panes de Leaflet (z-index ~1000). Sin glow (regla dura 4).
const lienzoWrapStyle: CSSProperties = {
  position: 'relative',
  height: '100%',
};

const chipUbicarStyle: CSSProperties = {
  position: 'absolute',
  top: '12px',
  left: '50%',
  transform: 'translateX(-50%)',
  zIndex: 1100,
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  maxWidth: 'calc(100% - 24px)',
  padding: `6px ${spacing[3]}`,
  background: colors.bg.secondary,
  border: `1px solid ${colors.primary}`,
  borderRadius: borderRadius.md,
  color: colors.fg.primary,
  fontSize: typography.fontSize.xs,
  fontFamily: typography.fontFamily.sans,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  pointerEvents: 'none',
};

// P2RED-S1-5b · chip «Buscando candidatos…»: mismo estilo que el de «Ubicar»
// pero anclado abajo-centro para no colisionar si ambos están activos. El
// borde se redeclara con el atajo completo (no `borderColor` suelto: React
// avisa al mezclar atajo y propiedad individual del mismo valor).
const chipCandidatosStyle: CSSProperties = {
  ...chipUbicarStyle,
  top: 'auto',
  bottom: '12px',
  border: `1px solid ${colors.border.DEFAULT}`,
  color: colors.fg.secondary,
};

// Progress Bar styles
const progressContainerStyle: CSSProperties = {
  background: colors.bg.tertiary,
  border: `1px solid ${colors.border.DEFAULT}`,
  borderRadius: borderRadius.lg,
  padding: '14px 20px',
  marginBottom: '16px',
};

const progressHeaderStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '10px',
  fontSize: typography.fontSize.base,
};

const progressHeaderLeftStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: GAP.md,
};

const progressTitleStyle: CSSProperties = {
  fontWeight: typography.fontWeight.semibold,
};

const progressSubtitleStyle: CSSProperties = {
  fontSize: typography.fontSize.xs,
  color: colors.fg.muted,
};

const progressPctStyle: CSSProperties = {
  fontFamily: typography.fontFamily.mono,  // F4.01: JetBrains Mono para números
  color: colors.scores.high,
  fontWeight: typography.fontWeight.semibold,
};

const progressTrackStyle: CSSProperties = {
  background: colors.border.DEFAULT,
  height: '8px',
  borderRadius: borderRadius.sm,
  overflow: 'hidden',
  display: 'flex',
};

const progressLegendStyle: CSSProperties = {
  display: 'flex',
  gap: '24px',
  marginTop: '10px',
  fontSize: typography.fontSize.xs,
  color: colors.fg.muted,
};

const legendItemStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: GAP.sm,
};

const legendDotStyle = (color: string): CSSProperties => ({
  width: '8px',
  height: '8px',
  borderRadius: borderRadius.full,
  background: color,
});

const legendCountStyle: CSSProperties = {
  fontFamily: typography.fontFamily.mono,  // F4.01: JetBrains Mono para números
  color: colors.fg.primary,
  fontWeight: typography.fontWeight.semibold,
};

// ============================================================================
// ICONS
// ============================================================================

function ListChecksIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width={ICON_SIZE.md}
      height={ICON_SIZE.md}
      stroke={colors.fg.muted}
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    >
      <path d="M11 18H3" />
      <path d="M11 12H3" />
      <path d="M11 6H3" />
      <path d="m15 9 2 2 4-4" />
      <path d="m15 15 2 2 4-4" />
    </svg>
  );
}

// ============================================================================
// A3x-5 · Barra de estado con coordenadas en vivo (ranura `barraEstado`)
// ============================================================================

/** Sumidero del cursor: el setter de estado interno de la barra. */
type SinkCursor = (coord: Coord25830 | undefined) => void;

interface BarraEstadoCoordsProps {
  /**
   * Registra el setter de `coordCursor` en el padre para que el `mousemove` del
   * lienzo actualice SOLO esta barra, sin re-renderizar Step2Page ni el subárbol
   * del mapa (cursor de alta frecuencia → aislamiento por `ref`).
   */
  registrarSinkCursor: (sink: SinkCursor) => void;
  coordSeleccion?: Coord25830;
  nUbicadas: number;
  nPorRevisar: number;
  nSinCoordenada: number;
}

/**
 * A3x-5 · Aísla el estado del cursor (alta frecuencia) del render de Step2Page.
 * `coordSeleccion` y los contadores (baja frecuencia, cambian al seleccionar o al
 * fijar coords) llegan por props; `coordCursor` vive aquí y lo actualiza el
 * sumidero registrado. Presentacionalmente delega en `StatusBar` (sin glow).
 */
const BarraEstadoCoords: FC<BarraEstadoCoordsProps> = ({
  registrarSinkCursor, coordSeleccion, nUbicadas, nPorRevisar, nSinCoordenada,
}) => {
  const [coordCursor, setCoordCursor] = useState<Coord25830 | undefined>(undefined);
  useEffect(() => {
    registrarSinkCursor(setCoordCursor);
    return () => registrarSinkCursor(() => { /* noop tras desmontar */ });
  }, [registrarSinkCursor]);
  return (
    <StatusBar
      coordCursor={coordCursor}
      coordSeleccion={coordSeleccion}
      nUbicadas={nUbicadas}
      nPorRevisar={nPorRevisar}
      nSinCoordenada={nSinCoordenada}
    />
  );
};

// ============================================================================
// P2RED S1-3b · Pie de ficha — «Confirmar elemento» + navegación entre elementos
// ============================================================================

interface PieFichaConfirmProps {
  /** El elemento ya está confirmado (chip informativo en lugar de acción). */
  confirmado: boolean;
  /** Confirmar (ausente ⇒ rol viewer: botón oculto, candado FB.10.17). */
  onConfirmar?: () => void;
  /** Posición 1-based del elemento en la tabla y total. */
  posicion: number;
  total: number;
  onAnterior?: () => void;
  onSiguiente?: () => void;
}

/**
 * Pie fijo de la ficha (prototipo M2, `ffoot`): «Confirmar elemento» ⇄
 * «Elemento confirmado» + Anterior/Siguiente con «N de M» (DOP-3=a). El flujo
 * de confirmación es el de P2-S3 (`WizardPageV2.handleConfirmElement`:
 * revalidateRecord + patch doble); aquí solo la superficie.
 */
const PieFichaConfirm: FC<PieFichaConfirmProps> = ({
  confirmado, onConfirmar, posicion, total, onAnterior, onSiguiente,
}) => {
  const btnNavStyle: CSSProperties = {
    display: 'inline-flex', alignItems: 'center', gap: '4px',
    background: 'transparent', border: 'none', color: colors.fg.muted,
    fontFamily: typography.fontFamily.sans, fontSize: typography.fontSize.xs,
    cursor: 'pointer', padding: `2px ${spacing[1]}`,
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: GAP.sm }}>
      {confirmado ? (
        <span
          data-testid="ficha-confirmado"
          style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
            padding: `6px ${spacing[2]}`, borderRadius: borderRadius.md,
            border: `1px solid ${colors.success}`, color: colors.success,
            fontSize: typography.fontSize.xs, fontWeight: 600,
          }}
        >
          <svg viewBox="0 0 24 24" width={13} height={13} fill="none" stroke="currentColor" strokeWidth={2.5} aria-hidden="true">
            <polyline points="20 6 9 17 4 12" />
          </svg>
          Elemento confirmado
        </span>
      ) : (
        onConfirmar != null && (
          <button
            type="button"
            onClick={onConfirmar}
            data-testid="ficha-btn-confirmar"
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
              padding: `7px ${spacing[2]}`, borderRadius: borderRadius.md,
              background: colors.primary, border: `1px solid ${colors.primary}`,
              color: colors.bg.primary, fontFamily: typography.fontFamily.sans,
              fontSize: typography.fontSize.xs, fontWeight: 600, cursor: 'pointer',
            }}
          >
            <svg viewBox="0 0 24 24" width={13} height={13} fill="none" stroke="currentColor" strokeWidth={2.5} aria-hidden="true">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            Confirmar elemento
          </button>
        )
      )}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <button type="button" style={btnNavStyle} onClick={onAnterior} disabled={onAnterior == null} data-testid="ficha-nav-anterior">
          ‹ Anterior
        </button>
        <span style={{ fontSize: typography.fontSize.xs, color: colors.fg.subtle, fontFamily: typography.fontFamily.mono }}>
          {posicion} de {total}
        </span>
        <button type="button" style={btnNavStyle} onClick={onSiguiente} disabled={onSiguiente == null} data-testid="ficha-nav-siguiente">
          Siguiente ›
        </button>
      </div>
    </div>
  );
};

// ============================================================================
// COMPONENT
// ============================================================================

export const Step2Page: FC<Step2PageProps> = ({
  documentContext,
  progress,
  // P2RED-S1-2 (RED-4): los props de las pestañas retiradas NO se destructuran
  // (quedan @deprecated en la interfaz para no romper consumidores).
  // P2RED-S1-3a: las 4 categorías SÍ se recuperan — dejan de ser datos de
  // pestaña y pasan a alimentar la proyección al contrato `Propuesta`.
  // P2RED-S1-4b (DOP-PUENTE=a): los props del puente sin-geo retirado tampoco
  // se destructuran (sinGeoInfrastructures, sinGeoFilterCounts, onAdd…, onExport,
  // onApplyCandidate, onOpenManualEdit, onEditCoords, onOpenViewer).
  corrections = [],
  suggestions = [],
  abreviaturas = [],
  diccionarioEntries = [],
  allInfrastructures = [],
  onConfirmElement,
  onDragEditCoords,
  onApplyManualCoords,
  isDemo = false,
  onContinue,
  rejectionBanner = null,
  duplicateCoordsWarning = [],
}) => {
  // Permisos de rol — FB.10.17
  const { canValidate } = usePermissions();

  // Estado banner
  const [bannerVisible, setBannerVisible] = useState(true);
  const [demoBannerVisible, setDemoBannerVisible] = useState(true); // F4.03

  // A3x-3/A3x-4 · selección espacial compartida mapa↔tabla + click-to-set.
  // `puedeFijar`: arma solo los registros sin coordenada final (`xFinal`/`yFinal`
  // ausentes); tras fijar, el sumidero rellena la coord y el predicado pasa a
  // false → el registro se auto-desarma.
  const infraPorId = useMemo(
    () => new Map(allInfrastructures.map((r) => [r.id, r])),
    [allInfrastructures],
  );
  // S1-4b · modo «Ubicar en el mapa» (DOP-S1-4B-UBICAR=a): el botón de la
  // ficha arma el click-to-set TAMBIÉN para registros con coordenada
  // (reubicación). MISMO sumidero 'map' que el arrastre del pin — sin canal
  // paralelo. Se desarma al fijar, con Esc o al cambiar la selección.
  const [modoUbicar, setModoUbicar] = useState(false);
  const puedeFijar = useCallback(
    (id: string): boolean => {
      const r = infraPorId.get(id);
      if (r == null) return false;
      if (modoUbicar) return true; // S1-4b: armado explícito, reubicación incluida
      return r.xFinal == null || r.yFinal == null;
    },
    [infraPorId, modoUbicar],
  );
  // `onFijarCoord`: persiste por el sumidero único de coordenadas con origen
  // 'map' (idéntico al arrastre del pin); la reproyección WGS84→EPSG:25830 ya la
  // hizo el hook (`pointToUTM30`). Coordenada en floats sin redondear.
  const onFijarCoord = useCallback(
    (id: string, coord: Coord25830) => {
      onDragEditCoords?.(id, coord.x, coord.y);
      setModoUbicar(false); // S1-4b: fijar consume el modo armado
    },
    [onDragEditCoords],
  );
  const { idSeleccion, seleccionar, manejarClicMapa } = useSeleccionMapaTabla({
    puedeFijar,
    onFijarCoord,
    onSeleccionarCambio: () => setModoUbicar(false), // S1-4b: cambiar de elemento desarma
  });

  // S1-4b · Esc desarma el modo «Ubicar en el mapa» desde cualquier foco.
  useEffect(() => {
    if (!modoUbicar) return;
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') setModoUbicar(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [modoUbicar]);

  // A3x-5 · Datos derivados para la `StatusBar` (baja frecuencia: cambian al
  // seleccionar o al fijar/persistir coords). P2VOC-b: la cuenta sale de la
  // clasificación única de la fila, la misma de la leyenda y de la tabla.
  const estadosFila = useMemo(() => contarEstadosFila(allInfrastructures), [allInfrastructures]);

  // ALBRTA-b2b · plazas del Registro de Turismo de Andalucía (OpenRTA) por fila. DOP-1 b: P2
  // pide el registro él mismo (una sola descarga por municipio: caché del servicio) y no toma
  // el hook de P3, que se carga a demanda. Nunca bloquea P2 (DOP-4).
  const ineRTA = documentContext.ine;
  const [cargaRTA, setCargaRTA] = useState<{ estado: 'cargando' } | ConsultaOpenRTA>({ estado: 'cargando' });
  useEffect(() => {
    let vivo = true;
    setCargaRTA({ estado: 'cargando' });
    getOpenRTAR2Service().consultarMunicipio(ineRTA).then((c) => { if (vivo) setCargaRTA(c); });
    return () => { vivo = false; };
  }, [ineRTA]);

  // VALMUNI-b2a · aviso de documento «sin validar contra el término municipal». DOP-3 a′: el
  // INE se resuelve como en la precarga (el del documento o, si falta, el del nombre). DOP-2
  // (ajuste «P2 escucha»): P2 no pide el límite; espera la petición de P1 o de la
  // geolocalización si sigue en curso, o lee su resultado. No bloquea ni baja la nota.
  const ineContorno = documentContext.ine
    || (documentContext.municipio ? getCodigoINE(documentContext.municipio) : null)
    || '';
  const [estadoContorno, setEstadoContorno] = useState<BoundaryLoadStatus>('no_pedido');
  useEffect(() => {
    let vivo = true;
    void escucharEstadoContorno(ineContorno).then((e) => { if (vivo) setEstadoContorno(e); });
    return () => { vivo = false; };
  }, [ineContorno]);
  const avisoSinValidarContorno = textoSinValidarContorno(
    documentContext.municipio || ineContorno,
    estadoContorno,
  );
  // Mismo filtro y conversión que P3 y P4; `toProcessedRecords` numera índice + 1, así que se
  // vuelve a la fila de P2 por posición en el mismo array filtrado.
  const plazasRTAPorId = useMemo<Map<string, PlazasRegistroFila> | null>(() => {
    if (cargaRTA.estado !== 'con_registro' || cargaRTA.datos.alojamientos.length === 0) return null;
    try {
      const filas = allInfrastructures.filter((i) => !i.esPlaceholder);
      const porRegistro = plazasRegistroPorFila(cargaRTA.datos.alojamientos, toProcessedRecords(filas));
      const porId = new Map<string, PlazasRegistroFila>();
      porRegistro.forEach((v, idRegistro) => {
        const fila = filas[idRegistro - 1];
        if (fila) porId.set(fila.id, v);
      });
      return porId;
    } catch (error) {
      console.warn('[ALBRTA-b2b] sin plazas del registro en la ficha: no se pudieron convertir las filas', error);
      return null;
    }
  }, [cargaRTA, allInfrastructures]);

  // P2RED S1-1 · datos de cabecera de la ficha (panel derecho) desde la selección.
  const elementoFicha = useMemo<FichaElemento | null>(() => {
    if (idSeleccion == null) return null;
    const r = infraPorId.get(idSeleccion);
    if (r == null) return null;
    const albergue = aptitudAlbergue(mapTipologia(r.tipo ?? '', r.nombre), r.nombre, r.aforoDeclarado);
    return {
      id: r.id,
      nombre: r.nombre,
      tipo: r.tipo,
      coordenada: r.xFinal != null && r.yFinal != null ? { x: r.xFinal, y: r.yFinal } : null,
      // ALBTIPO: misma tipología que la lista y la exportación (`mapTipologia`)
      albergue,
      // ALBFICHA-a: ficha de albergue del plan (Anexo VI), tal como la trae el documento
      fichaAlbergue: r.fichaAlbergue,
      // ALBCAP-a: capacidad con la regla del plan (DGPC 1989) y cotejo con el aforo declarado
      capacidadAlbergue: capacidadAlbergue(r.fichaAlbergue, r.aforoDeclarado, albergue.estado),
      // ALBRTA-b2b: plazas del registro (aparte; nunca se suman con el declarado ni la DGPC).
      // Cargando o sin red: solo en filas cuyo nombre dice alojamiento (DOP-4 b, lista estricta).
      registroRTA: (() => {
        const datos = plazasRTAPorId?.get(r.id);
        if (datos) return { estado: 'con_datos' as const, datos };
        if ((cargaRTA.estado === 'cargando' || cargaRTA.estado === 'fallo') && esAlojamientoPorNombre(r.nombre)) {
          return { estado: cargaRTA.estado };
        }
        return undefined;
      })(),
    };
  }, [idSeleccion, infraPorId, plazasRTAPorId, cargaRTA]);

  // S1-4a · ⌘V sobre fila seleccionada: el paste global se enruta al conversor
  // in-ficha (mismo camino de parseo que el botón «Pegar coordenada»). Se
  // ignora si no hay selección, si el rol no puede validar, o si el foco está
  // en un campo editable (el paste nativo manda).
  const coordSectionRef = useRef<CoordenadasSectionHandle>(null);

  useEffect(() => {
    if (elementoFicha == null || !canValidate || onApplyManualCoords == null) return;
    const onPaste = (e: ClipboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t != null && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      const texto = e.clipboardData?.getData('text') ?? '';
      if (texto.trim() === '') return;
      e.preventDefault();
      coordSectionRef.current?.pegarTexto(texto);
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [elementoFicha, canValidate, onApplyManualCoords]);

  // P2RED S1-3a · proyección de las 4 categorías al contrato `Propuesta` (§4
  // del AUDIT P2DPROP) + estado de sesión: cambios de status por propuesta y
  // excepciones por elemento (crítica 1). El estado vive aquí (contenedor);
  // la persistencia a traza/motor es cableado posterior (S4).
  const propuestasBase = useMemo(
    () => proyectarPropuestas({ corrections, suggestions, abreviaturas, diccionarioEntries }),
    [corrections, suggestions, abreviaturas, diccionarioEntries],
  );
  const [estadoPropuestas, setEstadoPropuestas] = useState<Record<string, PropuestaStatus>>({});
  const [overridesPropuestas, setOverridesPropuestas] = useState<
    Record<string, Record<string, ExcepcionElemento>>
  >({});
  const propuestas = useMemo<Propuesta[]>(
    () =>
      propuestasBase.map((p) => ({
        ...p,
        status: estadoPropuestas[p.id] ?? p.status,
        ...(overridesPropuestas[p.id] !== undefined && { overrides: overridesPropuestas[p.id] }),
      })),
    [propuestasBase, estadoPropuestas, overridesPropuestas],
  );
  const manejarEstadoPropuesta = useCallback((propuestaId: string, status: PropuestaStatus) => {
    setEstadoPropuestas((prev) => ({ ...prev, [propuestaId]: status }));
  }, []);
  const manejarExcepcion = useCallback(
    (propuestaId: string, infraId: string, excepcion: ExcepcionElemento | null) => {
      setOverridesPropuestas((prev) => {
        const deRegla = { ...(prev[propuestaId] ?? {}) };
        if (excepcion == null) {
          delete deRegla[infraId];
        } else {
          deRegla[infraId] = excepcion;
        }
        return { ...prev, [propuestaId]: deRegla };
      });
    },
    [],
  );

  // P2RED S1-3b · datos de la ficha que NO viajan en `FichaElemento`: registro
  // completo (score, fuente, confirmado) + posición/navegación en el orden
  // canónico de la tabla (Anterior/Siguiente, DOP-3=a).
  const registroSeleccion = idSeleccion != null ? infraPorId.get(idSeleccion) : undefined;
  // CATUSO · DOP-4=(a′): tipología PTEL del elemento seleccionado, por la vía que
  // midió CFU1 (`classification.type` del orquestador). `undefined` = sin opinión.
  const tipologiaSeleccion = infraTipologiaAPTEL(
    registroSeleccion?.geocoding?.classification?.type,
  );
  // CENFOR-B · DOP-1=(b): la ficha deja de recibir `source` crudo. Vía por
  // `deriveProcedencia` (misma que P1/P4) → etiqueta larga; si es centroide,
  // el aviso es el mismo texto que MOTIVO/OBSERVACIONES del entregable.
  const viaSeleccion =
    registroSeleccion != null
      ? deriveProcedencia(registroSeleccion, elementoFicha?.coordenada != null)
      : undefined;
  const fuenteSeleccion = viaSeleccion != null ? VIA_LABELS_LARGO[viaSeleccion] : undefined;
  const avisoSeleccion =
    viaSeleccion === 'centroide' && registroSeleccion != null
      ? avisoCentroide(registroSeleccion)
      : undefined;

  // P2RED S1-5a→5b (DOP-S1-5A-ALCANCE=c): candidatos on-demand del elemento
  // seleccionado, ploteados como capa efímera del lienzo (5b). El hook limpia
  // el estado al cambiar `infraId` → el ploteo lo sigue (DOP-S1-5B-LIMPIEZA=a).
  const { candidatos: candidatosSeleccion, cargando: cargandoCandidatos } =
    useCandidatosElemento({
      infraId: idSeleccion,
      nombre: registroSeleccion?.nombre ?? '',
      ...(registroSeleccion?.municipio ? { municipio: registroSeleccion.municipio } : {}),
      ...(documentContext.ine ? { codIne: documentContext.ine } : {}),
    });
  // 5b · proyección al contrato del lienzo: reproyección EPSG:25830→WGS84 aquí
  // (`pointToWGS84`, inversa exacta del `pointToUTM30` del click-to-set; el
  // mapa trabaja en lat/lng crudo).
  const candidatosMapa = useMemo<CandidatoMapa[]>(
    () =>
      candidatosSeleccion.map((c, idx) => {
        const { lat, lng } = pointToWGS84({ x: c.utmX, y: c.utmY });
        return {
          id: c.id ?? `cand-${idx}`,
          lat,
          lng,
          etiqueta: c.address,
          ...(c.source != null ? { fuente: String(c.source) } : {}),
          ...(c.isBest === true ? { destacado: true } : {}),
        };
      }),
    [candidatosSeleccion],
  );
  // 5b · despacho del «Aplicar» del popup — canal C2 con editSource 'assisted'
  // (confirmación explícita, DOP-S1-5B-INTERACCION=a). El candado viewer va en
  // el cableado de la prop (patrón U5 S1-4b): sin permiso, sin callback.
  const manejarAplicarCandidato = useCallback(
    (candidatoId: string) => {
      if (idSeleccion == null || onApplyManualCoords == null) return;
      const cand = candidatosSeleccion.find(
        (c, idx) => (c.id ?? `cand-${idx}`) === candidatoId,
      );
      if (cand != null) aplicarCandidato(idSeleccion, cand, onApplyManualCoords);
    },
    [idSeleccion, candidatosSeleccion, onApplyManualCoords],
  );
  const indiceSeleccion = useMemo(
    () => (idSeleccion == null ? -1 : allInfrastructures.findIndex((r) => r.id === idSeleccion)),
    [idSeleccion, allInfrastructures],
  );
  const navegarRelativo = useCallback(
    (delta: -1 | 1) => {
      if (indiceSeleccion < 0) return;
      const destino = allInfrastructures[indiceSeleccion + delta];
      if (destino != null) seleccionar(destino.id);
    },
    [indiceSeleccion, allInfrastructures, seleccionar],
  );

  const coordSeleccion = useMemo<Coord25830 | undefined>(() => {
    if (idSeleccion == null) return undefined;
    const r = infraPorId.get(idSeleccion);
    return r != null && r.xFinal != null && r.yFinal != null
      ? { x: r.xFinal, y: r.yFinal }
      : undefined;
  }, [idSeleccion, infraPorId]);

  // A3x-5 · Sumidero del cursor: el `mousemove` del lienzo (alta frecuencia) cae
  // en un `ref` estable que apunta al setter de `BarraEstadoCoords`; así solo se
  // re-renderiza la barra, no Step2Page ni el subárbol del mapa. La reproyección
  // WGS84→EPSG:25830 se hace aquí con `pointToUTM30` (floats sin redondear; el
  // redondeo es solo de presentación en `StatusBar`).
  const sinkCursorRef = useRef<SinkCursor>(() => { /* registrado al montar la barra */ });
  const registrarSinkCursor = useCallback((sink: SinkCursor) => {
    sinkCursorRef.current = sink;
  }, []);
  const manejarCursorMover = useCallback((latlng: MapPoint | null) => {
    sinkCursorRef.current(latlng != null ? pointToUTM30(latlng) : undefined);
  }, []);

  // P2RED-S1-2 · tabla canónica: filtro por estado (§7, los contadores son el
  // filtro) + buscador global (§6, «localiza y selecciona»: nunca filtra la
  // tabla; el «×» vacía el texto sin deseleccionar).
  const [filtroEstado, setFiltroEstado] = useState<FiltroEstado>('todos');
  const [textoBusqueda, setTextoBusqueda] = useState('');
  const coincidencias = useMemo(() => {
    const q = normalizarBusqueda(textoBusqueda);
    if (q === '') return [];
    return allInfrastructures.filter((r) =>
      normalizarBusqueda(r.nombre).includes(q) ||
      (r.tipo != null && normalizarBusqueda(r.tipo).includes(q)));
  }, [textoBusqueda, allInfrastructures]);
  const nombreSeleccionado = useMemo(() => {
    if (idSeleccion == null) return null;
    return coincidencias.find((r) => r.id === idSeleccion)?.nombre ?? null;
  }, [idSeleccion, coincidencias]);
  const seleccionarPrimeraCoincidencia = useCallback(() => {
    const primera = coincidencias[0];
    if (primera != null) seleccionar(primera.id);
  }, [coincidencias, seleccionar]);

  // Calcular porcentajes para la barra de progreso
  const total = progress.approved + progress.pending + progress.notGeolocated;
  const approvedPct = total > 0 ? (progress.approved / total) * 100 : 0;
  const pendingPct = total > 0 ? (progress.pending / total) * 100 : 0;
  const notGeoPct = total > 0 ? (progress.notGeolocated / total) * 100 : 0;
  const totalPct = total > 0 ? Math.round((progress.approved / total) * 100) : 0;

  // P2RED-S1-4b (DOP-PUENTE=a, Luis 15-jul): puente «Sin geolocalizar» RETIRADO
  // (cierra OBS-P2-PUENTE-SINGEO-RETIRAR-S13). Los banners informativos que
  // transportaba (PROP-D denominación genérica · UX-01 hidráulica sin cobertura)
  // reaparecen como avisos a nivel documento en S1-5/S1-VIS (OBS sucesora).
  // `SinGeolocalizarContent` y su árbol NO se borran aquí (poda en S4).

  return (
    <div style={containerStyle} data-p2-infra-count={allInfrastructures.length}>
      {/* Demo Banner — F4.03: patrón único dismissable, alineado con content (0 56px) */}
      {isDemo && (
        <div style={{ margin: '0 56px' }}>
          <InfoBanner
            variant="info"
            visible={demoBannerVisible}
            onClose={() => setDemoBannerVisible(false)}
          >
            <BannerStrong>Modo demo</BannerStrong>{' '}
            — Datos de ejemplo de Colomera, Granada (18051). Carga tu propio documento PTEL para procesar datos reales.
          </InfoBanner>
        </div>
      )}

      {/* Info Banner — GAP-P2-06 */}
      <div style={{ margin: '0 56px' }}>
        <InfoBanner
          variant="valida"
          visible={bannerVisible}
          onClose={() => setBannerVisible(false)}
        >
          {/* B09: mensaje contextual según estado real de la validación */}
          {progress.pending + progress.notGeolocated === 0 ? (
            <>
              <BannerStrong>Revisión completada.</BannerStrong>{' '}
              Todas las infraestructuras están ubicadas. Puedes continuar a Coteja.
            </>
          ) : progress.approved + progress.pending === 0 ? (
            <>
              <BannerStrong>Sin elementos geolocalizados.</BannerStrong>{' '}
              Ninguna infraestructura tiene coordenadas válidas. Usa «Edición manual» para introducirlas o revisa el documento de origen.
            </>
          ) : (
            <>
              <BannerStrong>Validación pendiente.</BannerStrong>{' '}
              {[
                progress.pending > 0 ? `${progress.pending} por revisar` : '',
                progress.notGeolocated > 0 ? `${progress.notGeolocated} sin coordenada` : '',
              ].filter(Boolean).join(' y ')}. Revísalas o continúa si son opcionales.
            </>
          )}
        </InfoBanner>
      </div>

      {/* VALMUNI-b2a (DOP-2 a): «sin validar contra el término municipal», bajo la cabecera.
          Aviso de documento, sin cerrar ni bloquear. */}
      {avisoSinValidarContorno && (
        <div style={{ margin: '8px 56px 0' }} data-testid="sin-validar-contorno-banner">
          <InfoBanner variant="valida" visible dismissible={false}>
            <BannerStrong>Sin validar contra el término municipal:</BannerStrong>{' '}
            {avisoSinValidarContorno.replace(/^Sin validar contra el término municipal: /, '')}
          </InfoBanner>
        </div>
      )}

      {/* P2CTX (8-oct-2026): la ContextBar del DS v2 se desmonta de P2. Repetía
          fichero · municipio · INE, que ya pinta la tira bajo la fila de pasos
          (DocumentStrip, WizardPageV2), y «N/total aprobados», que ya pinta la
          leyenda de «Progreso del documento» de esta página. */}

      {/* Step Content */}
      {/* A3x-1b · Camino 2: workspace map-primary — lienzo MapViewer overview
          (solo lectura) en la ranura central `mapa`; panel de trabajo (tabs)
          en `panelIzq` ensanchado. `breakpointEstrecho={0}` evita auto-ocultar
          el panel primario en pantallas estrechas. */}
      <div style={{ flex: 1, minHeight: 0, display: 'flex' }}>
        <WorkspaceShell
          alto="100%"
          breakpointEstrecho={0}
          anchoPanelIzq={460}
          barraEstado={
            <BarraEstadoCoords
              registrarSinkCursor={registrarSinkCursor}
              coordSeleccion={coordSeleccion}
              nUbicadas={estadosFila.ubicada}
              nPorRevisar={estadosFila.por_revisar}
              nSinCoordenada={estadosFila.sin_coordenada}
            />
          }
          principal={
            /* S1-4b · modo «Ubicar en el mapa»: cruceta + chip mientras está
               armado. El clic viaja por el MISMO `onLienzoClic` de siempre. */
            <div style={lienzoWrapStyle} data-modo-ubicar={modoUbicar ? 'true' : 'false'}>
              {modoUbicar && (
                <style>{`[data-modo-ubicar='true'] .leaflet-container { cursor: crosshair !important; }`}</style>
              )}
              {modoUbicar && (
                <div style={chipUbicarStyle} data-testid="chip-modo-ubicar" role="status">
                  Ubicando «{elementoFicha?.nombre ?? ''}» — un clic en el mapa fija la coordenada · Esc cancela
                </div>
              )}
              {/* 5b · aviso discreto mientras searchLocal resuelve (no captura punteros) */}
              {cargandoCandidatos && (
                <div style={chipCandidatosStyle} data-testid="chip-cargando-candidatos" role="status">
                  Buscando candidatos…
                </div>
              )}
              <MapViewer
                data={toNormalizedInfrastructures(allInfrastructures)}
                terminoIne5={ineContorno}
                height="100%"
                enableClustering
                showFilters={false}
                selectedInfraId={idSeleccion ?? undefined}
                onSelect={(infra) => { if (infra) seleccionar(infra.id); }}
                onLienzoClic={manejarClicMapa}
                onCursorMover={manejarCursorMover}
                candidatos={candidatosMapa}
                {...(canValidate && onApplyManualCoords != null
                  ? { onAplicarCandidato: manejarAplicarCandidato }
                  : {})}
              />
            </div>
          }
          panelIzq={
            <main style={panelTrabajoStyle}>
        {/* GUIA-DEVOLUCION-02: Banner ámbar de errores graves — dentro de main, en cabeza del panel de trabajo */}
        {rejectionBanner && (
          <div style={{ marginBottom: '12px', wordBreak: 'break-word' }} data-testid="rejection-banner">
            <InfoBanner
              variant="valida"
              visible
              dismissible={false}
            >
              <BannerStrong>
                Errores detectados ({rejectionBanner.code} · {rejectionBanner.percentage}% de infraestructuras).
              </BannerStrong>{' '}
              Se ha notificado al técnico municipal.{' '}
              <span style={{ color: colors.accent.amberLight }}>{rejectionBanner.instruccionEspecifica}</span>
            </InfoBanner>
          </div>
        )}

        {/* BUG-UI-16: Banner de coordenadas duplicadas — aviso cuando ≥ 3 infras comparten el mismo punto */}
        {duplicateCoordsWarning.length > 0 && (
          <div style={{ marginBottom: '12px' }} data-testid="duplicate-coords-banner">
            {duplicateCoordsWarning.map((group, idx) => (
              <div key={`dup-${idx}`} style={{ marginBottom: idx < duplicateCoordsWarning.length - 1 ? '6px' : 0 }}>
                <InfoBanner variant="valida" visible dismissible={false}>
                  <BannerStrong>
                    {group.names.length} infraestructuras comparten la misma posición
                    ({group.x.toFixed(0)} / {group.y.toFixed(0)}).
                  </BannerStrong>{' '}
                  Probable centroide municipal — revisa si las coordenadas son correctas:{' '}
                  <span style={{ fontFamily: typography.fontFamily.mono, fontSize: typography.fontSize.xs, color: colors.accent.amberLight }}>
                    {group.names.slice(0, 4).join(' · ')}{group.names.length > 4 ? ` · +${group.names.length - 4} más` : ''}
                  </span>
                </InfoBanner>
              </div>
            ))}
          </div>
        )}

        {/* Progress Bar */}
        <div style={progressContainerStyle}>
          <div style={progressHeaderStyle}>
            <div style={progressHeaderLeftStyle}>
              <ListChecksIcon />
              <strong style={progressTitleStyle}>Progreso del documento</strong>
              <span style={progressSubtitleStyle}>(<span style={{ fontFamily: typography.fontFamily.mono }}>{total}</span> elementos totales)</span>
            </div>
            <span style={progressPctStyle}>{totalPct}% completado</span>
          </div>

          <div style={progressTrackStyle}>
            <div
              style={{
                width: `${approvedPct}%`,
                background: colors.scores.high,
                transition: transitions.slow,
              }}
            />
            <div
              style={{
                width: `${pendingPct}%`,
                background: colors.scores.low,
                transition: transitions.slow,
              }}
            />
            <div
              style={{
                width: `${notGeoPct}%`,
                background: colors.scores.critical,
                transition: transitions.slow,
              }}
            />
          </div>

          <div style={progressLegendStyle} data-testid="p2-leyenda-estado">
            <div style={legendItemStyle}>
              <div style={legendDotStyle(colors.scores.high)} />
              <span><strong style={legendCountStyle}>{progress.approved}</strong> {progress.approved === 1 ? 'ubicada' : 'ubicadas'}</span>
            </div>
            <div style={legendItemStyle}>
              <div style={legendDotStyle(colors.scores.low)} />
              <span><strong style={legendCountStyle}>{progress.pending}</strong> por revisar</span>
            </div>
            <div style={legendItemStyle}>
              <div style={legendDotStyle(colors.scores.critical)} />
              <span><strong style={legendCountStyle}>{progress.notGeolocated}</strong> sin coordenada</span>
            </div>
          </div>
        </div>

        {/* Wrapper lock — FB.10.17: data-testid para tests de integración */}
        <div
          data-testid="step2-validate-lock"
          data-locked={String(!canValidate)}
          {...(!canValidate ? { 'aria-disabled': 'true' } : {})}
        >
          {/* Indicador solo lectura — visible para viewers (tokens DS v2) */}
          {!canValidate && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: `6px ${spacing[3]}`,
              marginBottom: spacing[3],
              background: colors.bg.tertiary,
              border: `1px solid ${colors.border.DEFAULT}`,
              borderRadius: borderRadius.md,
              fontSize: typography.fontSize.sm,
              color: colors.fg.muted,
              width: 'fit-content',
            }}>
              <svg viewBox="0 0 24 24" width={13} height={13} stroke="currentColor" strokeWidth={2} fill="none">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
              </svg>
              Solo lectura · Rol Viewer — puedes consultar pero no editar
            </div>
          )}

          {/* P2RED-S1-4b (DOP-PUENTE=a): puente «Sin geolocalizar» retirado — el
              trabajo por elemento vive en ficha (conversor/⌘V/«Ubicar»/confirmar)
              y tabla canónica; el wrapper lock FB.10.17 se conserva. */}
        </div>
            </main>
          }
          panelDer={
            /* P2RED S1-1 · ficha del elemento (RED-1=a): acordeón. S1-3a cabló
               Propuestas; S1-3b cablea Coordenadas (botonera, DOP-1=a),
               Historial (solo lectura Worker, DOP-2=a) y el pie «Confirmar
               elemento» + navegación (DOP-3=a). Catastro llega tras la revisión
               RED-6-CAT-UI; la edición de coordenadas en S1-4. */
            <FichaPanel
              elemento={elementoFicha}
              secciones={
                elementoFicha != null
                  ? {
                      coordenadas: (
                        <CoordenadasSection
                          ref={coordSectionRef}
                          coordenada={elementoFicha.coordenada}
                          score={registroSeleccion?.score}
                          fuente={fuenteSeleccion}
                          aviso={avisoSeleccion}
                          onAplicar={
                            canValidate && onApplyManualCoords != null
                              ? (x, y) => onApplyManualCoords(elementoFicha.id, x, y)
                              : undefined
                          }
                          onUbicar={
                            canValidate && onDragEditCoords != null
                              ? () => setModoUbicar((v) => !v)
                              : undefined
                          }
                          ubicarActivo={modoUbicar}
                        />
                      ),
                      propuestas: (
                        <PropuestasSection
                          propuestas={propuestas}
                          infraId={elementoFicha.id}
                          onEstado={canValidate ? manejarEstadoPropuesta : undefined}
                          onExcepcion={canValidate ? manejarExcepcion : undefined}
                        />
                      ),
                      /*
                       * `CATU` (20-ago-2026) · CAT-UI-CABLEADO. La ranura estaba
                       * en el contrato de `FichaPanel` desde `VIS-3b` y nadie la
                       * pasaba: la sección existía y el usuario no la alcanzaba.
                       *
                       * Se pasa solo si hay parcela. OJO — esto NO evita la sección
                       * vacía: `FichaPanel:218` recorre la lista fija `SECCIONES` y
                       * pinta las cinco cabeceras siempre, con marcador de posición
                       * dentro cuando la ranura falta. Verificado en pantalla el
                       * 20-ago (`CATU` §8): sin parcela sale «Disponible en próximas
                       * versiones del rediseño». La guarda solo evita construir un
                       * panel que devolvería `null`.
                       *
                       * `elementoCoord` (DOP-CATU-AVISO=b1) da la distancia
                       * parcela↔elemento. Medido hoy sobre el corpus de 5
                       * municipios: 111/220 = 50,5 % de adhesión, de las cuales
                       * `AUDIT_CFU1` §4.2 midió 34,2 % discordantes por uso. Sin
                       * esa línea, la ficha afirmaría sin fundamento y sin avisar.
                       *
                       * `CATUSO` (11-sep-2026) · DOP-CATUSO-4=(a′): `tipologia` sale
                       * de `infraTipologiaAPTEL(geocoding.classification.type)`, la
                       * misma composición que midió `AUDIT_CFU1` (no de
                       * `mapTipologia(tipo)`: sobre los tipos crudos de Alfacar
                       * —«BIC», «Centros de Enseñanza»— cae a GENERICO y no avisa).
                       * Sin geocoding → sin prop → sin opinión por uso (graceful).
                       */
                      ...(registroSeleccion?.catastroParcel != null
                        ? {
                            catastro: (
                              <CatastroInlinePanel
                                parcel={registroSeleccion.catastroParcel}
                                {...(elementoFicha.coordenada != null
                                  ? { elementoCoord: elementoFicha.coordenada }
                                  : {})}
                                {...(tipologiaSeleccion != null
                                  ? { tipologia: tipologiaSeleccion }
                                  : {})}
                              />
                            ),
                          }
                        : {}),
                      historial: <HistorialSection infraId={elementoFicha.id} />,
                    }
                  : undefined
              }
              pie={
                elementoFicha != null ? (
                  <PieFichaConfirm
                    confirmado={registroSeleccion?.confirmado === true}
                    onConfirmar={
                      canValidate && onConfirmElement != null
                        ? () => onConfirmElement(elementoFicha.id)
                        : undefined
                    }
                    posicion={indiceSeleccion + 1}
                    total={allInfrastructures.length}
                    onAnterior={indiceSeleccion > 0 ? () => navegarRelativo(-1) : undefined}
                    onSiguiente={
                      indiceSeleccion >= 0 && indiceSeleccion < allInfrastructures.length - 1
                        ? () => navegarRelativo(1)
                        : undefined
                    }
                  />
                ) : undefined
              }
            />
          }
          panelInf={
            /* P2RED-S1-2 · tabla canónica: buscador global (§6, localiza y
               selecciona) + chips-contador de estado (§7) + copiar-en-fila y
               lápiz→ficha (§11.2). La edición de coordenadas NUNCA es en celda
               (RED-2); llega por ficha/mapa en S1-4. */
            <AttributeTable
              filas={toFilasAtributo(allInfrastructures)}
              idSeleccion={idSeleccion ?? undefined}
              onSeleccionar={seleccionar}
              filtroEstado={filtroEstado}
              onFiltroEstado={setFiltroEstado}
              rol={canValidate ? 'editor' : 'lectura'}
              onEditar={seleccionar}
              cabecera={
                <BuscadorGlobal
                  valor={textoBusqueda}
                  onCambio={setTextoBusqueda}
                  nCoincidencias={coincidencias.length}
                  nombreSeleccionado={nombreSeleccionado}
                  onSeleccionarPrimera={seleccionarPrimeraCoincidencia}
                />
              }
            />
          }
        />
      </div>

      {/* C2-FIX: FooterBar eliminado — la navegación al paso 3 ya está en NavArrows
          (WizardPageV2). Mantener onContinue en la interfaz para tests y uso futuro. */}

      {/* OBS-UI-FAB-CONVERSOR-RETIRAR (FCR1, 15-jul): el FAB CONV-UX se retiró
          de Step 2 — el acceso al conversor UTM30 queda únicamente en el menú
          «Conversor UTM» del header persistente (HeaderSection, nunca se desmonta). */}
    </div>
  );
};

export default Step2Page;
